import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import React, { memo } from "react";
import { observer } from "mobx-react-lite";
import {
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Copy,
  Plus,
  Search,
} from "lucide-react";
import { ListChecks } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PageHeader,
  SearchableSelect,
  MetricCodeBadge,
} from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useTran } from "@/hooks";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth";
import { useOrganizationStore } from "@/features/organization";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { buildOrgStatusInfoMap } from "@/components/common/organization.utils";
import ManageMasterDataDialog from "../components/masterData/ManageMasterDataDialog";
import { masterDataService } from "../services";
import { showToast } from "@/lib/utils/toast";
import { PATHS } from "@/lib/router";
import { checkUserFromRole } from "@/helpers";
import ViewMasterDataDialog from "../components/masterData/ViewMasterDataDialog";
import { Layers } from "lucide-react";
import DeleteMasterDataDialog from "../components/masterData/DeleteMasterDataDialog";
import MetricsCloneDialog from "../components/masterData/MetricsCloneDialog";
import { Landmark } from "lucide-react";
import { OrgTreeNodeMultiFilter } from "../components/OrgTreeNodeMultiFilter";
import { Fragment } from "react";
import { dataEntryService } from "@/features/data-entry/services/data-entry.services";
import { reportingBoundaryService } from "@/features/reporting-boundary/services/reporting-boundary.services";
import MetricsAssignmentSummary from "../../../components/common/MetricsAssignmentSummary";
import { loadReportingYears } from "@/features/reporting-boundary/operations/reporting-boundary.ops";
import { formatReportingYearPeriod, MONTHS } from "../utils/period-constants";

const getCurrentCompanyId = (user) =>
  user?.companyId || user?.company?._id || user?.company?.id || user?.orgId;

const normalizeMasterDataItem = (item = {}) => {
  const disclosure = item.disclosure || {};
  const organizationObjects = Array.isArray(item.organizations)
    ? item.organizations
    : [];
  const organizations = organizationObjects
    .map((org) => org?.name || org?.label || org)
    .filter(Boolean);
  const organizationIds = organizationObjects
    .map((org) => org?.id || org?._id || org?.value || org)
    .filter(Boolean)
    .map((id) => String(id));

  return {
    id: item.id || item._id,
    disclosureId: disclosure.id || disclosure._id,
    reportingYear: Number.isFinite(Number(item.year))
      ? Number(item.year)
      : "N/A",
    framework: item.framework || disclosure.framework || "N/A",
    disclosureCode: disclosure.code || "-",
    disclosureTitle: disclosure.title || "-",
    disclosureCategory: disclosure.category || "-",
    organizations,
    organizationIds,
    useInTargetSetup: Boolean(item.useInTargetSetup),
    useInDataEntry: Boolean(item.useInDataEntry),
    isAllOrganization: Boolean(item.isAllOrganization),
    isCompanySelected: Boolean(item.isCompanySelected),
    createdAt: item.createdAt || null,
    updatedAt: item.updatedAt || null,
  };
};

const normalizeReportingBoundary = (data, companyId) => {
  const boundary = data?.reportingBoundary;
  if (!boundary) return null;

  const boundaryCompanyId = String(boundary.companyId || "");
  if (
    companyId &&
    boundaryCompanyId &&
    boundaryCompanyId !== String(companyId)
  ) {
    return null;
  }

  const frameworkCodes = Array.isArray(boundary.frameworkCodes)
    ? boundary.frameworkCodes
        .map((code) => String(code || "").trim())
        .filter(Boolean)
    : [];

  return {
    ...boundary,
    frameworkCodes,
  };
};

const normalizeFrameworkOption = (framework = {}) => {
  const code =
    framework.code ||
    framework.framework ||
    framework.value ||
    framework.id ||
    framework._id;
  if (!code) return null;

  return {
    code: String(code).trim(),
    name: String(framework.name || framework.label || code).trim(),
  };
};

const flattenOrgTree = (tree = []) => {
  const out = [];
  const visit = (nodes) => {
    (nodes || []).forEach((node) => {
      const value = node.id || node._id || node.key;
      const label = node.name || node.title || node.companyName || value;
      const unitLevel = node.unitLevel || node.unit_level;

      if (value && unitLevel !== "parent_company") {
        out.push({ value: String(value), label });
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        visit(node.children);
      }
    });
  };
  visit(tree);
  return out;
};

const CATEGORY_ORDER = [
  "General",
  "Environmental",
  "Social",
  "Governance",
  "Other",
];

const CATEGORY_STYLES = {
  General: {
    bg: "bg-purple-100",
    text: "text-purple-700",
    badge: "bg-purple-600",
  },
  Environmental: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    badge: "bg-emerald-600",
  },
  Social: { bg: "bg-blue-100", text: "text-blue-700", badge: "bg-blue-600" },
  Governance: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    badge: "bg-amber-600",
  },
  Other: { bg: "bg-slate-100", text: "text-slate-700", badge: "bg-slate-600" },
};

const normalizeCategory = (category) => {
  const normalized = String(category || "")
    .trim()
    .toLowerCase();
  const tokens = normalized.split(/[^a-z]+/).filter(Boolean);

  const hasAny = (values) => values.some((value) => tokens.includes(value));

  if (hasAny(["general", "gen"])) return "General";
  if (hasAny(["environment", "environmental", "env"])) return "Environmental";
  if (hasAny(["social", "soc"])) return "Social";
  if (hasAny(["governance", "gov"])) return "Governance";
  return "Other";
};

const CategorySection = React.memo(
  ({
    categoryKey,
    items,
    expandedCategories,
    toggleCategory,
    company,
    isUserLevel,
    permissions,
    setDeleteSelection,
    setManageRowId,
    setManageOpen,
    stopRenderRef,
    currentPath,
    handleOpenEdit,
    handleOpenRemove,
    handleOpenRemoveAll,
    setExpandedCategories,
    getFrameworkLabel,
    organizationTree,
    t,
  }) => {
    const style = CATEGORY_STYLES[categoryKey] || CATEGORY_STYLES.Other;
    const initial = categoryKey?.charAt(0)?.toUpperCase() || "?";
    const isExpanded = expandedCategories.has(categoryKey);

    const [visibleItemCount, setVisibleItemCount] = useState(0);

    const renderPathRef = useRef(currentPath);
    const itemRenderFrameRef = useRef(null);

    useEffect(() => {
      renderPathRef.current = currentPath;
    }, [currentPath]);

    const stopItemRender = useCallback(() => {
      if (itemRenderFrameRef.current) {
        cancelAnimationFrame(itemRenderFrameRef.current);
        itemRenderFrameRef.current = null;
      }
    }, []);

    const hasMultipleRemovableItems = useMemo(() => {
      let count = 0;
      for (const item of items) {
        if (!item.useInTargetSetup && !item.useInDataEntry) {
          count += 1;
          if (count > 1) return true;
        }
      }
      return false;
    }, [items]);

    const getRemovableItems = useCallback(
      () => items.filter((a) => !a.useInTargetSetup && !a.useInDataEntry),
      [items],
    );

    const shouldRenderItems = isExpanded;

    const visibleItems = useMemo(() => {
      if (!isExpanded) return [];

      return items.slice(0, visibleItemCount);
    }, [items, isExpanded, visibleItemCount]);

    useEffect(() => {
      stopItemRender();

      if (!isExpanded) {
        setVisibleItemCount(10);
        return;
      }

      // If items are small, show immediately
      if (items.length <= 10) {
        setVisibleItemCount(items.length);
        return;
      }

      setVisibleItemCount(10);

      let count = 10;

      const renderNextItems = () => {
        if (stopRenderRef.current) return;

        count += 10;

        setVisibleItemCount((prev) => Math.min(count, items.length));

        if (count < items.length) {
          itemRenderFrameRef.current = requestAnimationFrame(renderNextItems);
        }
      };

      itemRenderFrameRef.current = requestAnimationFrame(renderNextItems);

      return stopItemRender;
    }, [isExpanded, items.length, stopItemRender]);

    useEffect(() => {
      stopItemRender();

      if (!shouldRenderItems) {
        return stopItemRender;
      }

      let count = 10;
      const renderPath = renderPathRef.current;

      const renderNextItems = () => {
        if (stopRenderRef?.current) return;
        if (renderPathRef.current !== renderPath) return;

        count += 10;

        setVisibleItemCount((prev) => {
          if (prev >= items.length) return prev;
          return Math.min(count, items.length);
        });

        if (
          count < items.length &&
          !stopRenderRef?.current &&
          renderPathRef.current === renderPath
        ) {
          itemRenderFrameRef.current = requestAnimationFrame(renderNextItems);
        }
      };

      if (items.length > 10) {
        itemRenderFrameRef.current = requestAnimationFrame(renderNextItems);
      }

      return stopItemRender;
    }, [items.length, shouldRenderItems, stopItemRender, stopRenderRef]);

    useEffect(() => {
      if (items.length > 0 && isExpanded) {
        setVisibleItemCount(Math.min(10, items.length));
      }
    }, [items.length, isExpanded]);

    const handleToggleCategory = () => {
      stopItemRender();

      if (isExpanded) {
        setVisibleItemCount(0);
      }

      toggleCategory(categoryKey);
    };

    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleToggleCategory}
          aria-expanded={isExpanded}
          className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition hover:bg-muted/40"
        >
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${style.bg} ${style.text}`}
          >
            {initial}
          </span>

          <div className="flex flex-1 flex-col">
            <span className="text-sm font-semibold text-foreground">
              {t(categoryKey)}
            </span>
            <span className="text-xs text-muted-foreground">
              {items.length} {t("assignment(s)")}
            </span>
          </div>

          {hasMultipleRemovableItems && (
            <Can
              resource="master_data"
              action="delete"
              enabled={!isUserLevel}
              userPermissions={permissions}
            >
              <span
                className="ml-2 text-red-600 text-sm cursor-pointer hover:underline"
                onClick={(e) => {
                  setExpandedCategories(() => new Set());
                  e.stopPropagation();
                  stopItemRender();
                  const removableItems = getRemovableItems();
                  handleOpenRemoveAll(removableItems);
                }}
              >
                {t("Remove All")}
              </span>
            </Can>
          )}

          <Badge className={`${style.badge} text-white text-xs`}>
            {items.length}
          </Badge>

          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
        </button>

        {isExpanded && (
          <>
            <div className="flex flex-col gap-3 pl-2">
              {visibleItems.map((item) => (
                <MetricAssignmentItem
                  key={item.id}
                  item={item}
                  company={company}
                  isUserLevel={isUserLevel}
                  permissions={permissions}
                  setDeleteSelection={setDeleteSelection}
                  setManageRowId={setManageRowId}
                  setManageOpen={setManageOpen}
                  handleOpenEdit={handleOpenEdit}
                  handleOpenRemove={handleOpenRemove}
                  getFrameworkLabel={getFrameworkLabel}
                  organizationTree={organizationTree}
                  t={t}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  },
);
const MetricAssignmentItem = memo(function MetricAssignmentItem({
  item,
  company,
  isUserLevel,
  permissions,
  handleOpenEdit,
  handleOpenRemove,
  getFrameworkLabel,
  organizationTree,
  t,
}) {
  const orgStatusMap = useMemo(
    () => buildOrgStatusInfoMap(organizationTree),
    [organizationTree],
  );

  const filteredOrgs = useMemo(
    () =>
      (item.organizations || []).filter((org) => {
        const orgId = org?.id || org;
        return orgId !== company?.id;
      }),
    [item.organizations, company?.id],
  );

  const visibleOrganizations = useMemo(
    () => filteredOrgs.slice(0, 3),
    [filteredOrgs],
  );

  const haveOrgs =
    (item.isAllOrganization &&
      item.isCompanySelected &&
      visibleOrganizations.length === 0) ||
    visibleOrganizations.length > 0;

  const remainingCount = Math.max(
    0,
    filteredOrgs.length - visibleOrganizations.length,
  );
  const frameworkLabel = getFrameworkLabel?.(item.framework) || item.framework;

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex w-full flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <ListChecks className="h-5 w-5" />
        </span>

        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2">
            <MetricCodeBadge
              code={item.disclosureCode}
              category={item.disclosureCategory}
            />
            <span className="text-sm font-semibold text-foreground truncate">
              {item.disclosureTitle}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground sm:gap-3">
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {frameworkLabel}
            </span>

            <span className="flex items-center gap-1 text-primary font-medium">
              <CalendarDays className="h-3 w-3" />
              {item.reportingYear}
            </span>

            {!item.isAllOrganization && item.isCompanySelected ? (
              <span className="flex items-center gap-1 text-primary font-medium">
                <Building2 className="h-3 w-3" />
                {company?.name}
              </span>
            ) : null}

            {haveOrgs && (
              <span className="flex items-center gap-1">
                <Landmark className="h-3 w-3" />

                {item.isAllOrganization ? (
                  t("For all organization")
                ) : (
                  <div className="flex items-center gap-1 flex-wrap">
                    {visibleOrganizations.map((org, idx) => {
                      const orgId = org?.id || org;
                      const orgName =
                        typeof org === "string"
                          ? org
                          : org?.name || org?.label || org?.id;
                      const statusInfo = orgId
                        ? orgStatusMap.get(String(orgId))
                        : null;
                      const status = statusInfo?.status;
                      const inactiveDate = statusInfo?.inactiveDate;
                      return (
                        <span
                          key={String(orgId)}
                          className="flex items-center gap-0.5"
                        >
                          {orgName}
                          {status && (
                            <OrgStatusTag
                              status={status}
                              inactiveDate={inactiveDate}
                              className="text-[10px]"
                            />
                          )}
                        </span>
                      );
                    })}
                    {remainingCount > 0 && ` +${remainingCount}`}
                  </div>
                )}
              </span>
            )}
          </div>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 shrink-0 sm:w-auto sm:justify-end">
          {item?.useInTargetSetup && item?.useInDataEntry ? (
            <span className="text-muted-foreground text-xs">
              {t("Already used in Target Setup and Data Entry")}
            </span>
          ) : item?.useInTargetSetup ? (
            <span className="text-muted-foreground text-xs">
              {t("Already used in Target Setup")}
            </span>
          ) : item?.useInDataEntry ? (
            <span className="text-muted-foreground text-xs">
              {t("Already used in Data Entry")}
            </span>
          ) : (
            <Fragment>
              <Can
                resource="master_data"
                action="update"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="bg-sky-100 text-sky-700 hover:bg-sky-200"
                  onClick={() => handleOpenEdit(item.id, item?.reportingYear)}
                >
                  {t("Edit")}
                </Button>
              </Can>

              <Can
                resource="master_data"
                action="delete"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="px-1 text-destructive hover:bg-transparent hover:text-destructive/80"
                  onClick={() => handleOpenRemove(item)}
                >
                  {t("Remove")}
                </Button>
              </Can>
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
});

const MetricsAssignmentPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user, userRole, isJJSEA } = useAuthStore();
  const permissions = user?.permissions || [];
  const isUserLevel = checkUserFromRole(userRole);
  const {
    fetchHierarchy,
    tree: organizationTree,
    loading: organizationLoading,
  } = useOrganizationStore();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageRowId, setManageRowId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRowId, setViewRowId] = useState(null);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [reportingBoundary, setReportingBoundary] = useState(null);
  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [uniqueDisclosures, setUniqueDisclosures] = useState(0);

  const [reportingYears, setReportingYears] = useState([]);
  const [selectedReportingYear, setSelectedReportingYear] = useState(null);

  const [deleteSelection, setDeleteSelection] = useState([]);
  const company = organizationTree?.[0];

  const location = useLocation();
  const currentPath = location.pathname;

  const [visibleCategoryCount, setVisibleCategoryCount] = useState(1);

  const renderFrameRef = useRef(null);
  const stopRenderRef = useRef(false);
  const latestPathRef = useRef(currentPath);

  const fetchReportingYears = async () => {
    try {
      const data = await loadReportingYears();
      setReportingYears(data);
    } catch (error) {
      console.error("Failed to fetch reporting years:", error);

      showToast.error("Failed to load reporting years. Please try again.");

      setReportingYears([]); // safe fallback
    }
  };

  useEffect(() => {
    fetchReportingYears();
  }, []);

  useEffect(() => {
    latestPathRef.current = currentPath;
  }, [currentPath]);

  const stopCategoryRender = useCallback(() => {
    stopRenderRef.current = true;

    if (renderFrameRef.current) {
      cancelAnimationFrame(renderFrameRef.current);
      renderFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopCategoryRender();
    };
  }, [stopCategoryRender]);

  const [filters, setFilters] = useState({
    reportingYear: "All",
    framework: "All",
    search: "",
  });

  const [expandedCategories, setExpandedCategories] = useState(() => new Set());

  const userOrganizationIds = useMemo(() => {
    const ids = [];

    if (Array.isArray(user?.organizationUnitId)) {
      user.organizationUnitId.forEach((unit) => {
        if (typeof unit === "string" || typeof unit === "number") {
          ids.push(String(unit));
        } else {
          const id =
            unit?.id || unit?._id || unit?.organizationId || unit?.value;
          if (id) ids.push(String(id));
        }
      });
    }

    if (user?.assignedToParentCompany && user?.companyId) {
      ids.push(String(user.companyId));
    }

    return Array.from(new Set(ids)).sort();
  }, [
    user?.organizationUnitId,
    user?.assignedToParentCompany,
    user?.companyId,
  ]);
  const userOrganizationIdsKey = userOrganizationIds.join("|");
  const userOrganizationIdSet = useMemo(
    () =>
      new Set(userOrganizationIdsKey ? userOrganizationIdsKey.split("|") : []),
    [userOrganizationIdsKey],
  );
  const companyId = getCurrentCompanyId(user);
  const assignedToParentCompany = Boolean(user?.assignedToParentCompany);
  const organizationOptionsMap = useMemo(
    () =>
      new Map(
        flattenOrgTree(organizationTree).map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [organizationTree],
  );

  const frameworkNameByCode = useMemo(
    () =>
      new Map(
        availableFrameworks
          .map(normalizeFrameworkOption)
          .filter(Boolean)
          .map((framework) => [framework.code, framework.name]),
      ),
    [availableFrameworks],
  );

  const getFrameworkLabel = useCallback(
    (frameworkCode) => {
      const code = String(frameworkCode || "").trim();
      return frameworkNameByCode.get(code) || code;
    },
    [frameworkNameByCode],
  );

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [data, frameworkData] = await Promise.all([
        masterDataService.list(companyId ? { companyId } : {}, {
          throwOnError: false,
        }),
        reportingBoundaryService.getFrameworks({ throwOnError: false }),
      ]);

      setAvailableFrameworks(
        Array.isArray(frameworkData?.frameworks)
          ? frameworkData.frameworks
          : [],
      );
      const normalized = Array.isArray(data)
        ? data.map(normalizeMasterDataItem).filter((row) => Boolean(row.id))
        : [];

      const visibleItems = isUserLevel
        ? normalized.filter((row) => {
            if (row.isAllOrganization) return true;
            if (row.isCompanySelected && assignedToParentCompany) return true;
            return (row.organizationIds || []).some((id) =>
              userOrganizationIdSet.has(String(id)),
            );
          })
        : normalized;

      visibleItems.sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setItems(visibleItems);
    } catch (error) {
      //setReportingBoundary(null);
      setItems([]);
      showToast.error(error?.message || "Failed to load metric assignments.");
    } finally {
      setLoading(false);
    }
  }, [assignedToParentCompany, companyId, isUserLevel, userOrganizationIdSet]);

  const fetchDisclosures = async () => {
    const data = await dataEntryService.getMasterDataDisclosures();
    setUniqueDisclosures(data.pagination.total);
  };

  useEffect(() => {
    if (organizationTree.length > 0 || organizationLoading) return;
    fetchHierarchy().catch(() => {});
  }, [fetchHierarchy, organizationLoading, organizationTree.length]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    fetchDisclosures();
  }, [items.length]);

  // const formatReportingYearLabel = (ry) => {
  //   if (!ry?.startYear || !ry?.startMonth || !ry?.endYear || !ry?.endMonth) {
  //     return "";
  //   }

  //   const startMonthName = MONTHS[ry.startMonth - 1];
  //   const endMonthName = MONTHS[ry.endMonth - 1];

  //   const baseYear = ry.startYear;

  //   return `${baseYear} (${startMonthName} ${ry.startYear} - ${endMonthName} ${ry.endYear})`;
  // };

  const yearOptions = useMemo(() => {
    const itemYears = new Set(
      (items || [])
        .map((item) => Number.parseInt(item.reportingYear, 10))
        .filter(Number.isFinite),
    );

    const options = (reportingYears || [])
      .filter((ry) => ry?.startYear && itemYears.has(ry.startYear))
      .sort((a, b) => b.startYear - a.startYear)
      .map((ry) => ({
        value: String(ry.startYear),
        label: formatReportingYearPeriod(ry),
      }));

    return [{ value: "All", label: t("All") }, ...options];
  }, [reportingYears, items, t]);

  // To change framework and organization accourding to year change
  const yearFilteredItems = useMemo(() => {
    if (!selectedReportingYear?.startYear) return items;

    return items.filter(
      (item) =>
        Number(item.reportingYear) === Number(selectedReportingYear.startYear),
    );
  }, [items, selectedReportingYear]);

  const frameworkOptions = useMemo(() => {
    const frameworks = Array.from(
      new Set(
        yearFilteredItems
          .map((item) => String(item.framework || "").trim())
          .filter((framework) => framework && framework !== "N/A"),
      ),
    ).sort((a, b) => a.localeCompare(b));

    const options = frameworks.map((framework) => ({
      value: framework,
      label: getFrameworkLabel(framework),
      meta: getFrameworkLabel(framework) === framework ? "" : framework,
    }));

    // Only one framework -> remove "All"
    if (options.length === 1) {
      return options;
    }

    return [{ value: "All", label: t("All") }, ...options];
  }, [getFrameworkLabel, yearFilteredItems, t]);
  useEffect(() => {
    const availableFrameworks = frameworkOptions.filter(
      (option) => option.value !== "All",
    );

    setFilters((prev) => {
      const currentFrameworkExists = frameworkOptions.some(
        (option) => option.value === prev.framework,
      );

      let nextFramework = prev.framework;

      if (availableFrameworks.length === 1) {
        // Auto-select the only available framework
        nextFramework = availableFrameworks[0].value;
      } else if (!currentFrameworkExists) {
        // Current framework is unavailable for the selected reporting year
        nextFramework = "All";
      }

      // Avoid unnecessary state updates
      if (nextFramework === prev.framework) {
        return prev;
      }

      return {
        ...prev,
        framework: nextFramework,
      };
    });
  }, [frameworkOptions]);

  const hasItemWithAllOrgs = items.some((item) => item.isAllOrganization);

  const hasItemWithCompanySelected = items.some(
    (item) => item.isCompanySelected && !item.isAllOrganization,
  );

  const filteredItems = useMemo(() => {
    const normalizedSearch = filters.search.trim().toLowerCase();
    const selectedOrganizationIds = selectedOrganizations.map(String);

    return items.filter((item) => {
      // Year filter
      if (
        filters.reportingYear !== "All" &&
        String(item.reportingYear) !== String(filters.reportingYear)
      ) {
        return false;
      }

      // Framework filter
      if (filters.framework !== "All" && item.framework !== filters.framework) {
        return false;
      }

      // show  show Items that have specific orgniations and For All Organization
      // if (selectedOrganizationIds.length > 0) {
      //   if (isAllSelected) {
      //     if (!item.isAllOrganization) return false;
      //   } else {

      //     const itemOrganizationIds = (item.organizationIds || []).map(String);

      //     const hasMatch = selectedOrganizationIds.some((id) =>
      //       itemOrganizationIds.includes(id)
      //     );

      //     if (!item.isAllOrganization && !hasMatch) {
      //       return false;
      //     }
      //   }

      // }

      if (selectedOrganizationIds.length > 0) {
        const forAllOrgs = selectedOrganizationIds.includes(
          "for_all_organizations",
        );

        const isCompanySelected = selectedOrganizationIds.includes(
          String(company?.id),
        );

        const specificOrgs = selectedOrganizationIds.filter(
          (id) => id !== "for_all_organizations" && id !== String(company?.id),
        );

        const itemOrganizationIds = Array.isArray(item.organizationIds)
          ? item.organizationIds.map(String)
          : [];

        const matchesSpecificOrgs = specificOrgs.some((id) =>
          itemOrganizationIds.includes(id),
        );

        const showItem =
          // For All Organizations items:
          // show when user selects "for all", any organization, or company
          (item.isAllOrganization &&
            (forAllOrgs || specificOrgs.length > 0 || isCompanySelected)) ||
          // Specific organization items
          (matchesSpecificOrgs && !item.isAllOrganization) ||
          // Company selected items
          (isCompanySelected &&
            item.isCompanySelected &&
            !item.isAllOrganization);

        if (!showItem) return false;
      }
      // Search filter
      if (!normalizedSearch) return true;

      return [
        item.disclosureCode,
        item.disclosureTitle,
        item.framework,
        getFrameworkLabel(item.framework),
        item.reportingYear,
        item.disclosureCategory,
        ...(item.organizations || []),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [company?.id, filters, getFrameworkLabel, items, selectedOrganizations]);

  const uniqueAssignedMetrics = useMemo(() => {
    const keys = filteredItems
      .map(
        (item) =>
          item.disclosureId ||
          `${String(item.framework || "").trim()}:${String(
            item.disclosureCode || "",
          ).trim()}`,
      )
      .filter(Boolean);

    return new Set(keys).size;
  }, [filteredItems]);

  const filteredOrganizationIds = useMemo(() => {
    const ids = new Set();

    yearFilteredItems.forEach((item) => {
      //  Normal orgs
      if (!item.isAllOrganization) {
        (item.organizationIds || []).forEach((id) => {
          const normalizedId = String(id);

          if (!isUserLevel || userOrganizationIdSet.has(normalizedId)) {
            ids.add(normalizedId);
          }
        });
      }

      //  Add company (outer-most org) when condition met
      if (
        hasItemWithCompanySelected &&
        item.isCompanySelected &&
        (!isUserLevel || user?.assignedToParentCompany)
      ) {
        if (company?.id) {
          ids.add(String(company?.id));
        }
      }
    });

    return ids;
  }, [
    yearFilteredItems,
    company,
    hasItemWithCompanySelected,
    isUserLevel,
    user?.assignedToParentCompany,
    userOrganizationIdSet,
  ]);

  //Orginal Code for only show selected Items
  // const filteredOrganizationTree = useMemo(() => {
  //   const seen = new Set();

  //   const buildNode = (node) => {
  //     const nodeId = String(node.id || node._id || node.key);
  //     if (seen.has(nodeId)) return null;

  //     const children = (node.children || []).map(buildNode).filter(Boolean);

  //     // Only include node itself if it is selected
  //     if (filteredOrganizationIds.has(nodeId)) {
  //       seen.add(nodeId);
  //       return { ...node, children };
  //     }

  //     // If node itself is NOT selected, but has matching children,
  //     // we **promote the children** directly (skip this node)
  //     if (children.length > 0) {
  //       return children.length === 1 ? children[0] : children;
  //     }

  //     return null; // skip node entirely
  //   };

  //   const result = [];

  //   for (const node of organizationTree) {
  //     const built = buildNode(node);
  //     if (built) {
  //       // built could be an array if multiple children promoted
  //       if (Array.isArray(built)) {
  //         result.push(...built);
  //       } else {
  //         result.push(built);
  //       }
  //     }
  //   }

  //   return result;
  // }, [organizationTree, filteredOrganizationIds]);

  //Modify show both selected Items and their upper Level
  const filteredOrganizationTree = useMemo(() => {
    const seen = new Set();

    // STEP 1: collect selected + all parents
    const nodeMap = new Map();
    const parentMap = new Map();

    const buildMaps = (nodes, parent = null) => {
      (nodes || []).forEach((node) => {
        const id = String(node.id || node._id || node.key);

        nodeMap.set(id, node);

        if (parent) {
          parentMap.set(id, parent);
        }

        if (node.children?.length) {
          buildMaps(node.children, id);
        }
      });
    };

    buildMaps(organizationTree);

    // STEP 2: build allowed set (selected + parents)
    const allowedSet = new Set(filteredOrganizationIds);

    filteredOrganizationIds.forEach((id) => {
      let current = String(id);

      while (parentMap.has(current)) {
        const parent = parentMap.get(current);
        allowedSet.add(parent);
        current = parent;
      }
    });

    // STEP 3: your original builder (slightly adjusted)
    const buildNode = (node) => {
      const nodeId = String(node.id || node._id || node.key);
      if (seen.has(nodeId)) return null;

      const children = (node.children || []).map(buildNode).filter(Boolean);

      // include node if selected OR is ancestor
      if (allowedSet.has(nodeId)) {
        seen.add(nodeId);
        return { ...node, children };
      }

      // promote children if parent not needed
      if (children.length > 0) {
        return children.length === 1 ? children[0] : children;
      }

      return null;
    };

    const result = [];

    for (const node of organizationTree) {
      const built = buildNode(node);

      if (built) {
        if (Array.isArray(built)) {
          result.push(...built);
        } else {
          result.push(built);
        }
      }
    }

    return result;
  }, [organizationTree, filteredOrganizationIds]);

  const reportingPeriodLabel = useMemo(() => {
    const years = (reportingYears || [])
      .filter((ry) => Number.isFinite(Number(ry?.startYear)))
      .map((ry) => Number(ry.startYear));

    const ends = (reportingYears || [])
      .filter((ry) => Number.isFinite(Number(ry?.endYear)))
      .map((ry) => Number(ry.endYear));

    if (!years.length || !ends.length) {
      return t("Not defined");
    }

    const minYear = Math.min(...years);
    const maxYear = Math.max(...ends);

    return `${minYear}-${maxYear}`;
  }, [reportingYears, t]);

  const frameworksSummary = useMemo(() => {
    const frameworkSet = new Set();

    (reportingYears || []).forEach((ry) => {
      (ry?.frameworkCodes || []).forEach((fw) => {
        const cleaned = String(fw || "").trim();
        if (cleaned) frameworkSet.add(cleaned);
      });
    });

    return Array.from(frameworkSet)
      .sort((a, b) => getFrameworkLabel(a).localeCompare(getFrameworkLabel(b)))
      .map((framework) => ({
        code: framework,
        label: getFrameworkLabel(framework),
      }));
  }, [reportingYears, getFrameworkLabel]);

  const viewRow = useMemo(
    () => items.find((item) => item.id === viewRowId) || null,
    [items, viewRowId],
  );

  const manageRow = useMemo(
    () => items.find((item) => item.id === manageRowId) || null,
    [items, manageRowId],
  );

  const resetFilters = () => {
    stopCategoryRender();

    setFilters({
      reportingYear: "All",
      framework: "All",
      search: "",
    });

    setSelectedOrganizations([]);
  };

  const groupedItems = useMemo(() => {
    const grouped = new Map();

    filteredItems.forEach((item) => {
      const key = normalizeCategory(item.disclosureCategory);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(item);
    });

    return CATEGORY_ORDER.filter((key) => grouped.has(key)).map((key) => ({
      key,
      items: grouped.get(key) || [],
    }));
  }, [filteredItems]);

  const visibleGroupedItems = useMemo(() => {
    return groupedItems.slice(0, visibleCategoryCount);
  }, [groupedItems, visibleCategoryCount]);

  useEffect(() => {
    if (renderFrameRef.current) {
      cancelAnimationFrame(renderFrameRef.current);
      renderFrameRef.current = null;
    }

    if (loading || groupedItems.length === 0) {
      setVisibleCategoryCount(1);
      return;
    }

    const renderPath = currentPath;

    stopRenderRef.current = false;
    setVisibleCategoryCount(1);

    let index = 1;

    const renderNextCategory = () => {
      if (stopRenderRef.current) return;
      if (latestPathRef.current !== renderPath) return;

      index += 1;

      setVisibleCategoryCount((prev) => {
        if (prev >= groupedItems.length) return prev;
        return Math.min(index, groupedItems.length);
      });

      if (
        index < groupedItems.length &&
        !stopRenderRef.current &&
        latestPathRef.current === renderPath
      ) {
        renderFrameRef.current = requestAnimationFrame(renderNextCategory);
      }
    };

    if (groupedItems.length > 1) {
      renderFrameRef.current = requestAnimationFrame(renderNextCategory);
    }

    return () => {
      stopRenderRef.current = true;

      if (renderFrameRef.current) {
        cancelAnimationFrame(renderFrameRef.current);
        renderFrameRef.current = null;
      }
    };
  }, [loading, groupedItems, currentPath]);

  useEffect(() => {
    if (!isJJSEA) return;

    // Only initialize when nothing is selected or "All" is selected
    if (filters.framework && filters.framework !== "All") return;

    const jjGroup = frameworkOptions.find(
      (option) => option.value === "JJ-Group",
    );

    if (!jjGroup) return;

    setFilters((prev) => ({
      ...prev,
      framework: jjGroup.value,
    }));
  }, [isJJSEA, frameworkOptions]);
  const toggleCategory = useCallback((key) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const handleDelete = async (id) => {
    stopCategoryRender();

    const deletePromise = masterDataService.remove(id);

    showToast.promise(deletePromise, {
      loading: t("Removing metric assignment..."),
      success: t("Metric assignment removed successfully."),
      error: (error) =>
        error?.message || t("Failed to remove metric assignment."),
    });

    await deletePromise;

    setItems((prev) => {
      const removedRow = prev.find((row) => row.id === id);
      const updated = prev.filter((row) => row.id !== id);

      const category = normalizeCategory(removedRow?.disclosureCategory);

      const hasAllOrganizations = updated.some(
        (item) => item.isAllOrganization,
      );

      const remainingOrgIds = new Set(
        updated.flatMap((item) => item.organizationIds ?? []),
      );

      // Schedule the other state updates
      queueMicrotask(() => {
        setExpandedCategories(new Set([category]));

        setSelectedOrganizations((prevOrgs) =>
          prevOrgs.filter((orgId) =>
            orgId === "for_all_organizations"
              ? hasAllOrganizations
              : remainingOrgIds.has(orgId),
          ),
        );
      });

      return updated;
    });
  };

  const handleDeleteAll = async (items) => {
    stopCategoryRender();
    const ids = items
      .filter((a) => !a.useInTargetSetup && !a.useInDataEntry)
      .map((item) => item.id);
    if (!ids.length) return;

    // Create the promise
    const deletePromise = masterDataService.removeAllDisclosure(ids);

    // Show toast notifications
    showToast.promise(deletePromise, {
      loading: t("Removing metric assignments..."),
      success: t("Metric assignments removed successfully."),
      error: (error) =>
        error?.message || t("Failed to remove metric assignments."),
    });

    // Wait for deletion to complete
    await deletePromise;

    setItems((prev) => {
      const updated = prev.filter((row) => !ids.includes(row.id));

      // detect "all orgs"
      const hasAllOrganizations = updated.some(
        (item) => item.isAllOrganization,
      );

      // collect remaining org IDs
      const remainingOrgIds = new Set(
        updated.flatMap((item) => item.organizationIds || []),
      );

      // sync selected orgs (handles BOTH cases)
      setSelectedOrganizations((prevOrgs) =>
        prevOrgs.filter((orgId) => {
          if (orgId === "for_all_organizations") {
            return hasAllOrganizations;
          }
          return remainingOrgIds.has(orgId);
        }),
      );

      return updated;
    });
  };

  const handleAssignMetrics = useCallback(() => {
    stopCategoryRender();

    navigate(PATHS.ESG_MASTER_DATA_ASSIGN, {
      state: {
        frameworkCodes: reportingBoundary?.frameworkCodes || [],
        startYear: reportingBoundary?.startYear || 2019,
        endYear: reportingBoundary?.endYear || 2027,
      },
    });
  }, [navigate, reportingBoundary, stopCategoryRender]);

  const stopRenderNow = useCallback(() => {
    if (renderFrameRef.current) {
      cancelAnimationFrame(renderFrameRef.current);
      renderFrameRef.current = null;
    }
  }, []);

  const handleOpenEdit = useCallback(
    (id, year = null) => {
      setManageOpen(true);
      setManageRowId(id);
      stopRenderNow();

      if (year && reportingYears?.length) {
        const selected = reportingYears.find(
          (ry) => Number(ry.startYear) === Number(year),
        );

        setSelectedReportingYear(selected || null);
      }
    },
    [stopRenderNow, reportingYears],
  );

  const handleOpenRemove = useCallback(
    (item) => {
      setDeleteSelection([item]);
      stopRenderNow();
    },
    [stopRenderNow],
  );

  const handleOpenRemoveAll = useCallback(
    (items) => {
      const removableItems = items.filter(
        (item) => !item.useInTargetSetup && !item.useInDataEntry,
      );

      setDeleteSelection(removableItems);
      stopRenderNow();
    },
    [stopRenderNow],
  );

  const handleUpdate = async ({
    disclosureId,
    year,
    isAllOrganization,
    organizationIds,
  }) => {
    const payload = {
      year,
      isAllOrganization: Boolean(isAllOrganization),
      organizationIds: isAllOrganization ? [] : organizationIds || [],
      companyId:
        user?.companyId ||
        user?.company?._id ||
        user?.company?.id ||
        user?.orgId,
      period: {
        startYear: selectedReportingYear?.startYear,
        startMonth: selectedReportingYear?.startMonth,
        endYear: selectedReportingYear?.endYear,
        endMonth: selectedReportingYear?.endMonth,
      },
    };

    stopCategoryRender();
    const updatePromise = masterDataService.updateDisclosureById(
      disclosureId,
      payload,
    );

    showToast.promise(updatePromise, {
      loading: t("Saving changes..."),
      success: t("Metric assignment updated successfully."),
      error: (error) =>
        error?.response?.data?.message ||
        error?.message ||
        t("Failed to update metric assignment."),
    });

    await updatePromise;

    const nextOrganizationIds = isAllOrganization
      ? []
      : (organizationIds || []).map(String);

    const nextOrganizations = nextOrganizationIds.map(
      (orgId) => organizationOptionsMap.get(orgId) || orgId,
    );

    setItems((prev) => {
      const updated = prev.map((item) => {
        if (item.id === manageRowId) {
          const companyId = String(company?.id || "");
          const isCompanySelected = nextOrganizationIds.includes(companyId);

          return {
            ...item,
            reportingYear: year,
            isAllOrganization: Boolean(isAllOrganization),
            organizationIds: nextOrganizationIds,
            organizations: nextOrganizations,
            isCompanySelected,
          };
        }

        return item;
      });

      const updatedRow = updated.find((item) => item.id === manageRowId);

      const category = normalizeCategory(updatedRow?.disclosureCategory);

      setExpandedCategories(new Set([category]));

      return updated;
    });

    void fetchItems();
  };

  const reportingYearOrganizationIds = useMemo(() => {
    const orgIds = (selectedReportingYear?.organizations || []).map((org) =>
      String(org._id),
    );

    if (
      selectedReportingYear?.isCompanySelected &&
      companyId &&
      !selectedReportingYear?.isAllOrganization
    ) {
      orgIds.push(String(companyId));
    }

    return Array.from(new Set(orgIds));
  }, [selectedReportingYear, companyId]);

  const reportingYearOrganizationTree = useMemo(() => {
    if (!selectedReportingYear) return [];

    if (selectedReportingYear.isAllOrganization) {
      return organizationTree;
    }

    const allowedSet = new Set(reportingYearOrganizationIds.map(String));

    const getNodeId = (node) => String(node.id || node._id || node.key);

    const filterNode = (node) => {
      const children = (node.children || []).map(filterNode).filter(Boolean);

      const nodeId = getNodeId(node);
      const isAllowed = allowedSet.has(nodeId);

      // show node if it is allowed OR any child is allowed
      if (isAllowed || children.length > 0) {
        return {
          ...node,
          children,
        };
      }

      return null;
    };

    return (organizationTree || []).map(filterNode).filter(Boolean);
  }, [organizationTree, selectedReportingYear, reportingYearOrganizationIds]);

  return (
    <div className="space-y-3">
      <PageHeader
        title={t("Metric Assignment")}
        description={t(
          "Disclose your KPI data against the selected frameworks and reporting period.",
        )}
        icon={ListChecks}
        actions={
          <Can
            resource="master_data"
            action="create"
            enabled={!isUserLevel}
            userPermissions={permissions}
          >
            <div className="flex flex-col items-end gap-1">
              <Button
                onClick={handleAssignMetrics}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("Assign Metrics")}
              </Button>
            </div>
          </Can>
        }
      />

      {/* Temporary Hidden */}
      {/* <MetricsAssignmentSummary
        reportingPeriodLabel={reportingPeriodLabel}
        frameworksSummary={frameworksSummary}
        t={t}
      /> */}

      <Card className="gap-4 py-3">
        <CardHeader className="border-b pb-2">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Building2 className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    {t("Assigned KPIs")}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "View KPI assignments by organization, year, and framework.",
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <Can
                resource="master_data"
                action="create"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                <Button
                  variant="default"
                  onClick={() => {
                    setExpandedCategories(() => new Set());
                    setCloneOpen(true);
                  }}
                  disabled={items.length === 0}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {t("Clone Year")}
                </Button>
              </Can>
              <div
                className={`flex items-center px-2.5 py-1 rounded-full text-xs gap-2 transition-all duration-300 border border-sky-500 text-sky-500`}
              >
                <span>{t("Unique Assigned Metrics")}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full bg-sky-500 text-white`}
                >
                  {uniqueAssignedMetrics}
                </span>
              </div>
              <Badge className="rounded-full bg-emerald-500 px-2.5 py-1 text-white hover:bg-emerald-500">
                {filteredItems.length} {t("defined")}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 xl:grid-cols-12">
            <div className="space-y-1.5 xl:col-span-3">
              <p className="text-sm font-medium text-foreground">
                {t("Reporting Year")}
              </p>
              <SearchableSelect
                value={filters.reportingYear}
                onChange={(value) => {
                  stopCategoryRender();

                  const selected = (reportingYears || []).find(
                    (ry) => String(ry.startYear) === String(value),
                  );

                  setFilters((prev) => ({
                    ...prev,
                    reportingYear: value || "All",
                  }));

                  setSelectedReportingYear(selected || null);
                }}
                options={yearOptions}
                placeholder={t("Select Reporting Year")}
              />
            </div>
            <div className="space-y-1.5 xl:col-span-2">
              <p className="text-sm font-medium text-foreground">
                {t("Framework")}
              </p>
              <SearchableSelect
                value={filters.framework}
                onChange={(value) => {
                  stopCategoryRender();

                  setFilters((prev) => ({
                    ...prev,
                    framework: value || "All",
                  }));
                }}
                options={frameworkOptions}
                placeholder={t("Framework")}
                contentClassName="w-max min-w-[var(--radix-popover-trigger-width,var(--radix-select-trigger-width))] max-w-[calc(100vw-1rem)] [&_[data-slot=searchable-select-label]]:overflow-visible [&_[data-slot=searchable-select-label]]:whitespace-nowrap"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2 xl:col-span-4">
              <p className="text-sm font-medium text-foreground">
                {t("Organization")}
              </p>
              <OrgTreeNodeMultiFilter
                hasItemWithAllOrgs={hasItemWithAllOrgs}
                tree={filteredOrganizationTree}
                value={selectedOrganizations}
                onChange={(value) => {
                  stopCategoryRender();
                  setSelectedOrganizations(value);
                }}
                placeholder={t("Select organizations")}
                disabled={organizationLoading}
                filteredOrganizationIds={filteredOrganizationIds}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2 xl:col-span-2">
              <p className="text-sm font-medium text-foreground">
                {t("Search")}
              </p>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={filters.search}
                  onChange={(event) => {
                    stopCategoryRender();
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }));
                  }}
                  placeholder={t("Search by code or title...")}
                  className="pl-9"
                />
              </div>
            </div>
            {!isJJSEA && (
              <div className="space-y-1.5 xl:col-span-1">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetFilters}
                  className="h-auto w-full justify-start px-0 text-left text-sm text-destructive hover:text-destructive/80 xl:w-auto xl:justify-center"
                >
                  {t("Clear All Filters")}
                </Button>
              </div>
            )}
          </div>

          {/* <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={resetFilters}
              className="h-auto px-0 text-sm text-destructive hover:text-destructive/80"
            >
              {t("Clear")}
            </Button>
            <p className="text-xs text-muted-foreground">
              {filteredItems.length} {t("result(s)")}
            </p>
          </div> */}

          <div className="space-y-4">
            {loading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`metrics-assignment-skeleton-${index}`}
                  className="rounded-radius border border-border p-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-1 gap-3">
                      <Skeleton className="h-10 w-10 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-56" />
                        <Skeleton className="h-3 w-72" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Skeleton className="h-9 w-14 rounded-md" />
                      <Skeleton className="h-9 w-16 rounded-md" />
                    </div>
                  </div>
                </div>
              ))
            ) : filteredItems.length === 0 ? (
              <div className="rounded-radius border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
                {t("No metric assignment found.")}
              </div>
            ) : (
              visibleGroupedItems.map(({ key, items }) => (
                <CategorySection
                  key={key}
                  categoryKey={key}
                  items={items}
                  expandedCategories={expandedCategories}
                  toggleCategory={toggleCategory}
                  company={company}
                  isUserLevel={isUserLevel}
                  permissions={permissions}
                  setDeleteSelection={setDeleteSelection}
                  setManageRowId={setManageRowId}
                  setManageOpen={setManageOpen}
                  stopRenderRef={stopRenderRef}
                  currentPath={currentPath}
                  handleOpenEdit={handleOpenEdit}
                  handleOpenRemove={handleOpenRemove}
                  handleOpenRemoveAll={handleOpenRemoveAll}
                  setExpandedCategories={setExpandedCategories}
                  getFrameworkLabel={getFrameworkLabel}
                  organizationTree={organizationTree}
                  t={t}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <ViewMasterDataDialog
        open={viewOpen}
        onOpenChange={(open) => {
          setViewOpen(open);
          if (!open) {
            setViewRowId(null);
          }
        }}
        row={viewRow}
      />

      {/*Delete Dialog */}
      {deleteSelection.length > 0 && (
        <DeleteMasterDataDialog
          open={deleteSelection.length > 0}
          onOpenChange={() => {
            stopRenderRef.current = false;
            setDeleteSelection([]);
          }}
          item={deleteSelection.length === 1 ? deleteSelection[0] : null}
          items={deleteSelection}
          handleDelete={handleDelete}
          handleDeleteAll={handleDeleteAll}
        />
      )}

      <MetricsCloneDialog
        open={cloneOpen}
        onOpenChange={setCloneOpen}
        items={items}
        loading={loading}
        startYear={reportingBoundary?.startYear}
        endYear={reportingBoundary?.endYear}
        organizationTree={organizationTree}
        onCloned={fetchItems}
        reportingYears={reportingYears}
      />

      <ManageMasterDataDialog
        open={manageOpen}
        onOpenChange={(open) => {
          stopRenderRef.current = false;
          setManageOpen(open);
          if (!open) setManageRowId(null);
        }}
        row={manageRow}
        company={company}
        organizationTree={reportingYearOrganizationTree}
        reportingYearOrganizationIds={reportingYearOrganizationIds}
        organizationLoading={organizationLoading}
        reportingYears={reportingYears}
        selectedReportingYear={selectedReportingYear}
        setSelectedReportingYear={setSelectedReportingYear}
        onSave={handleUpdate}
      />
    </div>
  );
});

export default MetricsAssignmentPage;
